import {
  sendVerificationResponseSchema,
  verifyVerificationResponseSchema,
  type SendVerificationRequest,
  type SendVerificationResponse,
  type VerifyVerificationRequest,
  type VerifyVerificationResponse,
} from "@openings/api-contract";
import { BaseHttpClient, baseOk, ApiOk } from "./base";

const sendCodeResponse = baseOk(sendVerificationResponseSchema);
const verifyCodeResponse = baseOk(verifyVerificationResponseSchema);

export type SendVerificationInput = Omit<SendVerificationRequest, "purpose"> & {
  purpose?: string;
};
export interface SendCancelVerificationInput {
  appointmentId: string;
}
export type VerifyCodeInput = Omit<VerifyVerificationRequest, "purpose"> & {
  purpose?: string;
};

export type SendVerificationResult = ApiOk<SendVerificationResponse>;
export type VerifyCodeResult = ApiOk<VerifyVerificationResponse>;

/**
 * Client for verification code flows (send, cancel verification, verify code).
 * Methods are backward-compatible with prior positional string arguments while
 * preferring structured input objects going forward.
 */
export class VerificationsClient extends BaseHttpClient {
  /**
   * Send a verification code to a phone number.
   * Usage (new): client.verifications.send({ phoneNumber })
   * Usage (legacy): client.verifications.send(phoneNumber)
   */
  send(input: SendVerificationInput): Promise<SendVerificationResult>;
  send(phoneNumber: string): Promise<SendVerificationResult>;
  send(inputOrPhone: SendVerificationInput | string) {
    const body: SendVerificationRequest =
      typeof inputOrPhone === "string"
        ? { phoneNumber: inputOrPhone, purpose: "general" }
        : {
            ...inputOrPhone,
            purpose: inputOrPhone.purpose ?? "general",
          };
    return this.post("/v1/verifications/send", body, sendCodeResponse);
  }

  /**
   * Send a verification code destined for appointment cancellation.
   * Usage (new): client.verifications.sendForCancel({ appointmentId })
   * Usage (legacy): client.verifications.sendForCancel(appointmentId)
   */
  sendForCancel(
    input: SendCancelVerificationInput,
  ): Promise<SendVerificationResult>;
  sendForCancel(appointmentId: string): Promise<SendVerificationResult>;
  sendForCancel(inputOrId: SendCancelVerificationInput | string) {
    const appointmentId =
      typeof inputOrId === "string" ? inputOrId : inputOrId.appointmentId;
    return this.post(
      "/v1/verifications/send",
      { purpose: `cancel:${appointmentId}`, appointmentId },
      sendCodeResponse,
    );
  }

  /**
   * Verify a code previously sent to a phone number.
   * Usage (new): client.verifications.verify({ phoneNumber, code })
   * Usage (legacy): client.verifications.verify(phoneNumber, code)
   */
  verify(input: VerifyCodeInput): Promise<VerifyCodeResult>;
  /**
   * @deprecated Use the object form: `verify({ phoneNumber, code })`.
   */
  verify(phoneNumber: string, code: string): Promise<VerifyCodeResult>;
  verify(
    inputOrPhone: VerifyCodeInput | string,
    maybeCode?: string,
  ): Promise<VerifyCodeResult> {
    const body: VerifyVerificationRequest =
      typeof inputOrPhone === "string"
        ? {
            phoneNumber: inputOrPhone,
            code: maybeCode as string,
            purpose: "general",
          }
        : {
            ...inputOrPhone,
            purpose: inputOrPhone.purpose ?? "general",
          };
    return this.post("/v1/verifications/verify", body, verifyCodeResponse);
  }
}
